import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiTag } from 'react-icons/fi';
import { toast } from '../../utils/customToast';
import Modal from '../shared/Modal';

const MessageSortModal = ({
    open,
    onClose,
    selectedMessages,
    customTabs,
    onAddToTab,
    onRemoveFromTab,
    canUseCustomTabs
}) => {
    const [selectedTabs, setSelectedTabs] = useState(new Set());
    const [loading, setLoading] = useState(false);

    const cancelRef = useRef(null);
    const confirmRef = useRef(null);

    // Calculer les tabs visibles et memoizer le résultat pour éviter les re-renders inutiles
    const visibleTabs = useMemo(() => {
        return customTabs.filter(tab => tab.isVisible !== false);
    }, [customTabs]);

    useEffect(() => {
        if (open && selectedMessages.size > 0 && customTabs.length > 0) {
            // Pré-sélectionner les onglets qui contiennent déjà certains des messages sélectionnés
            const messageIds = Array.from(selectedMessages);
            const tabsContainingMessages = new Set();

            customTabs.forEach(tab => {
                if (tab.messageIds && messageIds.some(id => tab.messageIds.includes(id))) {
                    tabsContainingMessages.add(tab._id);
                }
            });

            setSelectedTabs(tabsContainingMessages);
        } else {
            setSelectedTabs(new Set());
        }
    }, [open, selectedMessages, customTabs]);

    // Écouter les mises à jour des onglets personnalisés
    useEffect(() => {
        const handleCustomTabsUpdate = () => {
            // Forcer le re-render en mettant à jour une clé fictive
            // Ceci garantit que le composant se re-rend avec les nouveaux tabs
        };

        window.addEventListener('customTabsUpdated', handleCustomTabsUpdate);
        return () => window.removeEventListener('customTabsUpdated', handleCustomTabsUpdate);
    }, [customTabs]);

    const toggleTab = (tabId) => {
        setSelectedTabs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tabId)) {
                newSet.delete(tabId);
            } else {
                newSet.add(tabId);
            }
            return newSet;
        });
    };

    const handleSave = async () => {
        if (loading) return;

        setLoading(true);
        const messageIds = Array.from(selectedMessages);

        try {
            // Déterminer quels onglets ajouter/enlever
            const currentTabs = new Set();
            customTabs.forEach(tab => {
                if (tab.messageIds && messageIds.some(id => tab.messageIds.includes(id))) {
                    currentTabs.add(tab._id);
                }
            });

            // Onglets à ajouter (sélectionnés mais pas actuellement assignés)
            const tabsToAdd = Array.from(selectedTabs).filter(id => !currentTabs.has(id));

            // Onglets à enlever (actuellement assignés mais non sélectionnés)
            const tabsToRemove = Array.from(currentTabs).filter(id => !selectedTabs.has(id));

            // Effectuer les opérations
            const promises = [];

            tabsToAdd.forEach(tabId => {
                promises.push(onAddToTab(tabId, messageIds));
            });

            tabsToRemove.forEach(tabId => {
                promises.push(onRemoveFromTab(tabId, messageIds));
            });

            await Promise.all(promises);

            const messageCount = messageIds.length;
            const messageText = messageCount > 1 ? 'messages' : 'message';
            const tabCount = selectedTabs.size;

            if (tabCount === 0) {
                toast.success(`${messageCount} ${messageText} retiré(s) de tous les onglets`);
            } else {
                const tabText = tabCount > 1 ? 'onglets' : 'onglet';
                toast.success(`${messageCount} ${messageText} organisé(s) dans ${tabCount} ${tabText}`);
            }

            onClose();
        } catch (error) {
            toast.error(error.message || 'Erreur lors de l\'organisation des messages');
        } finally {
            setLoading(false);
        }
    };

    if (!open || !canUseCustomTabs) return null;

    const messageCount = selectedMessages.size;

    // Modal actions
    const modalActions = (
        <>
            {visibleTabs.length > 0 ? (
                <>
                    <button
                        ref={cancelRef}
                        onClick={onClose}
                        className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
                        disabled={loading}
                    >
                        Annuler
                    </button>

                    <button
                        ref={confirmRef}
                        onClick={handleSave}
                        disabled={loading}
                        className="flex-1 px-4 py-2 bg-primary text-white dark:text-black rounded hover:bg-primary-hover transition disabled:opacity-50"
                    >
                        {loading ? (
                            <>
                                Organisation...
                            </>
                        ) : (
                            <>
                                Organiser
                            </>
                        )}
                    </button>
                </>
            ) : (
                <button
                    ref={cancelRef}
                    onClick={onClose}
                    className="w-full bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
                >
                    Retour
                </button>
            )}
        </>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Organiser les messages"
            actions={modalActions}
            initialFocusRef={confirmRef}
        >
            {/* Content */}
            <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {messageCount} message{messageCount > 1 ? 's' : ''} sélectionné{messageCount > 1 ? 's' : ''}.
                    Choisissez les onglets où les organiser :
                </p>

                {visibleTabs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <FiTag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Aucun onglet personnalisé disponible</p>
                        <p className="text-sm mt-1">Créez d'abord des onglets pour organiser vos messages</p>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {visibleTabs.map((tab) => {
                            const isSelected = selectedTabs.has(tab._id);
                            const messageCountInTab = tab.messageIds?.length || 0;

                            return (
                                <label
                                    key={tab._id}
                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${isSelected
                                        ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleTab(tab._id)}
                                        className="w-4 h-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:ring-2"
                                    />
                                    <div
                                        className="w-4 h-4 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: tab.color }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-900 dark:text-white truncate">
                                                {tab.name}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                                {messageCountInTab} message{messageCountInTab !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal >
    );
};

export default MessageSortModal;